'use client';

import Text from '@shared/components/content/text';
import { Icons } from '@shared/components/content/icons';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { Separator } from '@shared/components/ui/separator';
import type { Column } from '@tanstack/react-table';
import * as React from 'react';
import { DataTableToolbarFilter } from './data-table-toolbar';
import { FilterPillDensityProvider } from './filter-pill-density';

interface DataTableFiltersPanelProps<TData> {
  /** Every filterable column, in toolbar order. */
  columns: Column<TData>[];
  onClearAll: () => void;
}

/**
 * Every filter of a table in one panel, one labelled row each, behind a
 * "Filters" button that counts what is set. The quick-access pills on the
 * toolbar share the same column state, so a choice made here lights the
 * matching pill and vice versa; this is the complete view, not a second
 * set of filters.
 */
export function DataTableFiltersPanel<TData>({
  columns,
  onClearAll,
}: DataTableFiltersPanelProps<TData>) {
  const [open, setOpen] = React.useState(false);
  // A column can be filterable without a toolbar control; those have no row.
  // Nor does a search box: it belongs on the row, not among the facets.
  const rows = columns.filter(
    (column) =>
      column.columnDef.meta?.variant && column.columnDef.meta.variant !== 'text'
  );
  const activeCount = rows.filter(
    (column) => column.getFilterValue() !== undefined
  ).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Icons.listFilter className="mr-2 size-4" />
          Filters
          {activeCount > 0 && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />
              <Badge
                variant="secondary"
                className="rounded-sm border-transparent bg-primary-500 px-1 font-normal text-gray-00"
              >
                {activeCount}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[26rem] p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <Text variant="subSection">Filters</Text>
          <Button
            variant="ghost"
            size="sm"
            disabled={activeCount === 0}
            onClick={() => onClearAll()}
          >
            Clear all
          </Button>
        </div>
        <FilterPillDensityProvider value="compact">
          <div className="flex flex-col gap-3 p-4">
            {rows.map((column) => (
              <div
                key={column.id}
                className="grid grid-cols-[7rem,1fr] items-center gap-3"
              >
                <Text variant="subtle" className="text-sm">
                  {column.columnDef.meta?.label ?? column.id}
                </Text>
                <div className="flex min-w-0">
                  <DataTableToolbarFilter column={column} />
                </div>
              </div>
            ))}
          </div>
        </FilterPillDensityProvider>
      </PopoverContent>
    </Popover>
  );
}
